import type { ContentMenuPage } from '../api/types';

import { HUB_SECTIONS, type HubSectionDef } from './hubSections';

/**
 * A node in the hub's navigation.
 *
 * Two kinds share this shape. Most nodes are real pages. The rest are grouping
 * levels the path implies but no visible page occupies — `firearm-licence` has
 * 27 pages beneath it and is not itself a visible page, and there are 20 such
 * levels in the current import. Those get a node so their children are still
 * reachable, and carry `navigable: false` so the menu renders them as a heading
 * rather than a link that would 404.
 */
export interface ContentMenuNode {
  /** A page id, or `group:<path>` for a synthesised level. */
  id: string;
  title: string;
  /** The materialised path, e.g. `sport-shooting/postal-shooting`. */
  slugFull: string;
  /** False when no page backs this level — render as a heading, not a link. */
  navigable: boolean;
  /** Null sorts last. */
  position: number | null;
  children: ContentMenuNode[];
  /**
   * Real pages beneath this node, at any depth, not counting the node itself.
   *
   * The same figure `HubMenuSection.pageCount` reports for a section, computed by
   * the same pass — so a tally in a menu row and a tally in the Knowledge centre
   * legend answer the identical question, and cannot drift apart. Synthesised
   * grouping levels are scaffolding and are not counted.
   */
  pageCount: number;
}

/** One top-level entry: a fixed section, with whatever the data puts beneath it. */
export interface HubMenuSection {
  key: string;
  label: string;
  /** Path the entry links to. */
  to: string;
  children: ContentMenuNode[];
  /**
   * Real pages beneath the section, at any depth. Synthesised grouping levels are
   * not counted — they are an artefact of the path, not something a member can
   * read — so this answers "how much is in here", which is the only question a
   * count on a legend row is asked.
   */
  pageCount: number;
}

const segments = (slugFull: string): string[] => (slugFull || '').split('/').filter(Boolean);

/**
 * Siblings order by position, then title — matching the admin tree, so what an
 * admin arranges is what a member sees. Synthesised levels have no position of
 * their own and inherit the lowest one beneath them, so a group sorts where its
 * contents would have.
 */
const compareNodes = (a: ContentMenuNode, b: ContentMenuNode): number => {
  const pa = a.position ?? Number.MAX_SAFE_INTEGER;
  const pb = b.position ?? Number.MAX_SAFE_INTEGER;
  if (pa !== pb) {
    return pa - pb;
  }
  return a.title.localeCompare(b.title);
};

/**
 * Fallback label for a synthesised level: `nhsa-annual-general-meetings` reads
 * as "Nhsa Annual General Meetings". Crude, and it only ever shows for a level
 * with no page behind it — authoring that page replaces it with a real title.
 */
const titleFromSlug = (slug: string): string =>
  slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Builds the hub's menu: the fixed sections from `hubSections.ts`, each filled
 * with the pages whose path falls under one of the prefixes it owns.
 *
 * The hierarchy comes from `slugFull`, not `parentPageId`. The parent chain is
 * unusable here — `fetchMenuPages` asks for visible and active pages only, and
 * most section rows in the import are neither, so their children arrive with a
 * parent that isn't in the set. The path is materialised from that same chain,
 * survives the parent being hidden, and is what the URL already uses.
 */
export const buildHubMenu = (
  items: ContentMenuPage[],
  sections: HubSectionDef[] = HUB_SECTIONS,
): HubMenuSection[] => {
  // Which section claims a given first segment. Built once; a prefix claimed
  // twice belongs to whichever section declares it first.
  const owner = new Map<string, HubSectionDef>();
  sections.forEach((section) => {
    section.owns.forEach((prefix) => {
      if (!owner.has(prefix)) {
        owner.set(prefix, section);
      }
    });
  });

  // Every node by full path, so a page and the levels above it meet up
  // regardless of which order the flat list arrives in.
  const byPath = new Map<string, ContentMenuNode>();
  const rootsOf = new Map<string, ContentMenuNode[]>();
  sections.forEach((section) => rootsOf.set(section.key, []));

  /** Returns the node at `path`, creating the chain above it if needed. */
  const ensure = (path: string, sectionKey: string): ContentMenuNode => {
    const existing = byPath.get(path);
    if (existing) {
      return existing;
    }

    const parts = segments(path);
    const node: ContentMenuNode = {
      id: `group:${path}`,
      title: titleFromSlug(parts[parts.length - 1] ?? path),
      slugFull: path,
      navigable: false,
      position: null,
      children: [],
      pageCount: 0,
    };
    byPath.set(path, node);

    if (parts.length <= 1) {
      rootsOf.get(sectionKey)?.push(node);
    } else {
      ensure(parts.slice(0, -1).join('/'), sectionKey).children.push(node);
    }
    return node;
  };

  items.forEach((item) => {
    const parts = segments(item.slugFull);
    if (!parts.length) {
      return;
    }

    const section = owner.get(parts[0]);
    // Not claimed by any section: still reachable by URL and search, just not
    // in the nav. This is what keeps the bar a fixed size.
    if (!section) {
      return;
    }

    // `ensure` may have already made this a placeholder on the way down to a
    // child; promote it to the real page rather than adding a duplicate.
    const node = ensure(item.slugFull, section.key);
    node.id = item.id;
    node.title = item.title;
    node.navigable = true;
    node.position = item.position;
  });

  // A synthesised level sorts where its contents would.
  const inheritPosition = (node: ContentMenuNode): number | null => {
    const own = node.position;
    const fromChildren = node.children
      .map(inheritPosition)
      .filter((value): value is number => value !== null);
    if (own !== null) {
      return own;
    }
    if (!fromChildren.length) {
      return null;
    }
    node.position = Math.min(...fromChildren);
    return node.position;
  };

  const sortRecursive = (nodes: ContentMenuNode[]): void => {
    nodes.sort(compareNodes);
    nodes.forEach((node) => sortRecursive(node.children));
  };

  /**
   * Stamps every node with what it holds, and returns what it *contributes* to the
   * level above — its own descendants plus itself, when a member can actually open
   * it. Groups are scaffolding, so they contribute only their contents.
   *
   * One bottom-up pass rather than a `countPages(node.children)` per node, which
   * would re-walk every subtree once per ancestor: against the live tree's 1,415
   * rows at six levels deep that is the difference between a linear pass and a
   * quadratic one, on the render path of the menu.
   */
  const stampCounts = (node: ContentMenuNode): number => {
    node.pageCount = node.children.reduce((sum, child) => sum + stampCounts(child), 0);
    return node.pageCount + (node.navigable ? 1 : 0);
  };

  return sections.map((section) => {
    const roots = rootsOf.get(section.key) ?? [];

    // The section's own prefix is the section — nesting it inside itself would
    // read "Natshoot › Natshoot", and the bar entry already links there. Its
    // children are lifted a level. The section's *other* owned prefixes keep
    // their node and stay as named groups, because they are genuinely separate
    // bodies of content: flattening `nhsa-info` would tip 19 unrelated pages
    // into Natshoot's dropdown with nothing to tell them apart.
    const ownIndex = roots.findIndex((node) => node.slugFull === section.slug);
    const children =
      ownIndex === -1
        ? roots
        : [...roots.slice(0, ownIndex), ...roots[ownIndex].children, ...roots.slice(ownIndex + 1)];

    children.forEach(inheritPosition);
    sortRecursive(children);

    // A section whose own landing page hasn't been authored would link to a
    // 404, so it falls through to the first thing under it instead. The entry
    // itself never disappears — the bar's size is fixed by design.
    const own = byPath.get(section.slug);
    const fallback = children.find((child) => child.navigable) ?? children[0];
    const to = own?.navigable ? section.slug : (fallback?.slugFull ?? section.slug);

    // The section's own landing page has been lifted out of `children` above, so it
    // isn't in this figure — the count is what the section *holds*, and the section
    // is not a thing inside itself.
    return {
      key: section.key,
      label: section.label,
      to,
      children,
      // The same pass that stamps each child, so the section's figure and its
      // children's figures are guaranteed to be the same arithmetic.
      pageCount: children.reduce((sum, child) => sum + stampCounts(child), 0),
    };
  });
};
